import { gsap } from "./gsap.js";

export const reduceObject = (obj, callback, base = {}, ...additionalArgs) =>
  Object.entries(obj).reduce((acc, entry) => {
    return callback(acc, entry, ...additionalArgs);
  }, base);

export function* mapEntries(
  obj,
  callback,
  callbackParam = "entry",
  original = obj,
  key = "",
  parentKeys = [],
) {
  const keys = [...parentKeys, key !== "" ? key : null].filter(Boolean);
  const runCallback = ([entryKey, entryValue]) => {
    switch (callbackParam) {
      case "key":
        entryKey = callback(entryKey, keys, original);
        break;
      case "value":
        entryValue = callback(entryValue, keys, original);
        break;
      default:
        return callback([entryKey, entryValue], keys, original);
    }
    return [entryKey, entryValue];
  };
  if (typeof obj !== "object") {
    if (obj === original) {
      throw new Error("Cannot map entries of non-object");
    }
    yield runCallback([key, obj]);
    return;
  }

  for (const entry of Object.entries(obj)) {
    const [entryKey, entryValue] = entry;
    if (Array.isArray(entryValue)) {
      entry[1] = entryValue.flatMap((item) =>
        typeof item === "object"
          ? Object.fromEntries([
              ...mapEntries(
                item,
                callback,
                callbackParam,
                original,
                entryKey,
                keys,
              ),
            ])
          : item,
      );
    } else if (typeof entryValue === "object") {
      entry[1] = Object.fromEntries([
        ...mapEntries(
          entryValue,
          callback,
          callbackParam,
          original,
          entryKey,
          keys,
        ),
      ]);
    }
    const result = runCallback(entry);
    if (Array.isArray(result) && Array.isArray(result[0])) {
      yield* result;
    } else {
      yield result;
    }
  }
}
export const mapObjectEntries = (obj, callback, callbackParam = "entry") =>
  Object.fromEntries([...mapEntries(obj, callback, callbackParam)]);
export const getType = (obj) =>
  Object.prototype.toString.call(obj).slice(8, -1).toLowerCase();
export const _mergeObjects = (obj1, obj2) => {
  const clone = structuredClone(obj1);
  for (const [key, value] of Object.entries(obj2)) {
    const type = getType(value);
    if (
      clone[key] !== void 0 &&
      getType(clone[key]) === type &&
      ["array", "object"].includes(type)
    ) {
      clone[key] = deepMerge(clone[key], value);
    } else {
      clone[key] = structuredClone(value);
    }
  }

  return clone;
};
export const deepMerge = (...objs) => {
  let clone = structuredClone(objs.shift());
  for (const obj of objs) {
    const type = getType(obj);
    if (getType(clone) !== type) {
      clone = structuredClone(obj);
      continue;
    }
    if (type === "array") {
      clone = [...clone, ...structuredClone(obj)];
    } else if (type === "object") {
      clone = _mergeObjects(clone, obj);
    } else {
      clone = obj;
    }
  }

  return clone;
};
export const dimensionalize = (obj, separator = ".") => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    const keys = key.split(separator);
    let current = acc;
    keys.forEach((k, i) => {
      if (i === keys.length - 1) {
        current[k] = value;
      } else {
        current[k] = current[k] || {};
        current = current[k];
      }
    });
    return acc;
  }, {});
};
export const trimFloat = (value, digits = 2) =>
  Number.parseFloat(value.toFixed(digits));
export const definitionPatterns = (definitions) =>
  reduceObject(definitions, (acc, [key, { pattern }]) => ({
    ...acc,
    [key]: pattern,
  }));
export const valueTypeDefinition = (definitions, value) => {
  for (const definition of Object.values(definitions)) {
    if (definition.pattern.test(value)) return definition;
  }

  console.warn(
    "No matching value type definition found for value:",
    value,
    "Definitions:",
    definitionPatterns(definitions),
  );
  return null;
};
export const valueTypeDefinitionProperty = (property, definitions, value) => {
  var _a;
  return (_a = valueTypeDefinition(definitions, value)) == null
    ? void 0
    : _a[property];
};
export const getValueTypeParser = curry(valueTypeDefinitionProperty)("parser");
export const cssParseValue = (definitions, value, { element, property } = {}) => {
  const parser = getValueTypeParser(definitions, value);
  if (!parser) {
    console.warn(
      "No matching parser found for value:",
      value,
      "Definitions:",
      definitionPatterns(definitions),
    );
    return null;
  }

  return parser(value, { element, property });
};
export const cssParseViewportValue = (axis, value) => {
  const blockAxisKeys = ["y", "vb", "vh", "h", "vertical", "block"];
  const windowSizeKey = blockAxisKeys.includes(axis)
    ? "innerHeight"
    : "innerWidth";
  const windowSize = window[windowSizeKey];
  return trimFloat(windowSize * (Number.parseFloat(value) / 100));
};
export const CSS_DIMENSION_TYPES = {
  rem: {
    pattern: /^\d*(?:\.\d+)?(rem)$/,
    parser: (value) =>
      Number.parseFloat(value) *
      ((window == null ? void 0 : window.remBase) ?? 10),
  },
  em: {
    pattern: /^\d*(?:\.\d+)?(em)$/,
    parser: (value, { element }) =>
      Number.parseFloat(value) * getStyleProperty(element, "font-size"),
  },
  vb: {
    pattern: /^\d*(?:\.\d+)?(vb|vh)$/,
    parser: curry(cssParseViewportValue)("block"),
  },
  vi: {
    pattern: /^\d*(?:\.\d+)?(vi|vw)$/,
    parser: curry(cssParseViewportValue)("inline"),
  },
  px: {
    pattern: /^\d+(?:\.\d+)?(px)?$/,
    parser: (value) => Number.parseInt(value, 10),
  },
};
export const cssParseDimensionValue = partial(cssParseValue, CSS_DIMENSION_TYPES);
export const CSS_VALUE_TYPES = {
  duration: {
    pattern: /^\d+(?:\.\d+)?m?s$/,
    parser: (value) =>
      Number.parseFloat(value) * (value.endsWith("ms") ? 1 : 10),
  },
  dimension: {
    pattern: /^\d+(?:\.\d+)?[a-zA-Z]+$/,
    parser: cssParseDimensionValue,
  },
  number: {
    pattern: /^\d+(?:\.\d+)?$/,
    parser: (value) => Number.parseInt(value, 10),
  },
  array: {
    pattern: /^\S+(?: \S+)+$/,
    parser: (value) => value.split(" ").map(cssParsePropertyValue),
  },
  string: {
    pattern: /^.*$/,
    parser: (value) => value,
  },
};
export const cssParsePropertyValue = partial(cssParseValue, CSS_VALUE_TYPES);
export const stickyHeader = document.querySelector("sticky-header");

export const imagesLoaded = async (
  _elements,
  { container = false, root = document.documentElement } = {},
) => {
  const elements =
    typeof _elements === "string"
      ? root.querySelectorAll(_elements)
      : _elements;
  const getImages = (el) => {
    if (!isIterable(el)) {
      return [...el.querySelectorAll("img")];
    }
    return [...el].reduce((acc, el2) => {
      acc.push(...getImages(el2));
      return acc;
    }, []);
  };
  const images = container ? getImages(elements) : [...elements];
  const lazyImages = images.filter((image) => image.loading !== "eager");
  for (const image of lazyImages) {
    image.setAttribute("loading", "eager");
  }

  return await Promise.allSettled(images.map((image) => image.decode()));
};
export const initAnchor = (anchor) => {
  if (!anchor || anchor.dataset.initialized) return;
  if (!/^#\D/.test(anchor.hash) || EXCLUDED_ANCHOR_LINKS.includes(anchor.hash))
    return;
  const handler = (e) => {
    e.preventDefault();
    const anchorEl = document.querySelector(anchor.hash);
    if (!anchorEl) return;
    const offset =
      stickyHeader.offsetHeight +
      Number.parseInt(
        window.getComputedStyle(anchorEl).getPropertyValue("scroll-margin-top"),
        10,
      );
    smoothScroll(anchorEl, { offset });
  };
  anchor.addEventListener("click", handler);
  anchor.dataset.initialized = true;
  const remove = () => anchor.removeEventListener("click", handler);
  return { target: anchor, handler, remove };
};
export const getScrollProp = (container, axis) => {
  const axes = {
    x: container === window ? "X" : "Left",
    y: container === window ? "Y" : "Top",
  };
  return `scroll${axes[axis]}`;
};
export const getSizeProp = (container, axis) => {
  const prefix = container === window ? "inner" : "offset";
  const prop = axis === "y" ? "Height" : "Width";
  return `${prefix}${prop}`;
};
export function smoothScroll(
  target,
  {
    container = window,
    axis = "y",
    offset = 0,
    targetBounds = null,
    alignToClosestEdge = false,
    jump = false,
    onComplete = () => {},
  } = {},
  {
    distanceMax = 5e3,
    ease,
    durationEase = "power2.out",
    divisorScale = 1.15,
    distanceMinMultiplier = 0.3,
  } = {},
) {
  var _a;
  ease ?? (ease = blendEases("none", "power2.out", "power4.out"));
  const containerScrollProp = getScrollProp(container, axis);
  const containerSizeProp = getSizeProp(container, axis);
  const start = container[containerScrollProp];
  let end = target;
  if (end instanceof HTMLElement) {
    targetBounds ?? (targetBounds = end.getBoundingClientRect());
    end = start + targetBounds[axis];
    if (
      alignToClosestEdge &&
      targetBounds[axis] > container[containerSizeProp] / 2
    ) {
      end -=
        container[containerSizeProp] -
        targetBounds[axis === "y" ? "height" : "width"];
      offset *= -1;
    }
  }

  const scrollContainer = target.closest("[data-scroll-container]");
  if (scrollContainer) distanceMax = scrollContainer.offsetHeight;
  const duration = jump
    ? 0
    : calculateScrollDuration(start, end - offset, {
        distanceMax,
        ease: durationEase,
      });
  document.body.classList.add("is-scrolling");
  (_a = document.querySelector("sticky-header")) == null
    ? void 0
    : _a.dispatchEvent(new Event("preventHeaderReveal"));
  gsap.to(container, {
    duration,
    ease,
    scrollTo: {
      [axis]: end,
      [`offset${axis.toUpperCase()}`]: offset,
    },
    onComplete: () => {
      document.body.classList.remove("is-scrolling");
      onComplete();
    },
  });
}
export function calculateScrollDuration(
  start,
  end,
  {
    distanceMax = 5e3,
    divisorScale = 1.15,
    distanceMinMultiplier = 0.3,
    ease = "power2.out",
  } = {},
) {
  const rawDistance = Math.abs(end - start);
  const divisor = distanceMax * divisorScale;
  const distanceMin = divisor * distanceMinMultiplier;
  const distance = ease
    ? gsap.utils.pipe(
        gsap.utils.mapRange(0, distanceMax, 0, 1),
        gsap.parseEase(ease),
        gsap.utils.mapRange(0, 1, 0, distanceMax),
      )(rawDistance)
    : rawDistance;
  const duration = clamp(distanceMin, distance, distanceMax) / divisor;
  return duration;
}
export function parseElementString(elementString) {
  switch (elementString) {
    case "document":
      return document;
    case "window":
      return window;
    default:
      return elementString;
  }
}
export function parseElementsArg(
  elements,
  { container = document, callbackArgs = [] } = {},
) {
  const parsedElements = parseElementString(elements);
  if (Array.isArray(parsedElements)) {
    if (parsedElements[0] instanceof HTMLElement) return parsedElements;
    return [
      ...parsedElements.map((selector3) =>
        parseElementsArg(selector3, { container, callbackArgs }),
      ),
    ];
  }

  if (typeof parsedElements === "string")
    return [...container.querySelectorAll(parsedElements)];
  if (isIterable(parsedElements)) return [...parsedElements];
  if (typeof parsedElements === "function")
    return parsedElements(...callbackArgs);
  console.warn("Unrecognized elements argument:", parsedElements);
  return null;
}
export function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}
export const trapFocusHandlers = {};
export function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];
  removeTrapFocus();
  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;
    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };
  trapFocusHandlers.focusout = () => {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };
  trapFocusHandlers.keydown = (event) => {
    if (event.code.toUpperCase() !== "TAB") return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };
  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);
  elementToFocus.focus();
  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}
export function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}
export function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;
  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}
export const copyAttributes = (from, to) => {
  const attributes = from.attributes;
  for (const { name, value } of attributes) {
    to.setAttribute(name, value);
  }
};
window.copyAttributes = copyAttributes;
export const changeElementTag = (el, type) => {
  const newElement = document.createElement(type);
  copyAttributes(el, newElement);
  newElement.innerHTML = el.innerHTML;
  el.replaceWith(newElement);
};
window.changeElementTag = changeElementTag;
export const ON_CHANGE_DEBOUNCE_TIMER = 300;
export const RESIZE_DEBOUNCE_TIMER = 250;
export const SLIDE_CHANGE_DEBOUNCE_TIMER = 300;
export const BREAKPOINT_KEY_SEGMENTS = ["breakpoint", "bp", "media", "mq"];
export const KEY_PATTERNS = {
  breakpoint: new RegExp(
    `^(?:${BREAKPOINT_KEY_SEGMENTS.join("|")})-|-(?:${BREAKPOINT_KEY_SEGMENTS.join("|")})$`,
  ),
};
export const VALUE_PATTERNS = {
  boolean: /^(true|false)$/,
  number: /^\d+(\.\d+)?$/,
  array: /^\[(.*)\]$/,
  arrayString: /^(?:[\w\- ]+, ?)+[\w\- ]+,?$/,
  object: /^\{(?:.*?\:.*?,?\s*)+\}$/,
};
export const MEDIA_ASPECT_RATIO_ALIASES = {
  landscape: "16/9",
  portrait: "5/7",
  square: "1/1",
};
export const MEDIA_ASPECT_RATIO_DELIMITERS = ["/", ":"];
export const VALUE_PARSERS = {
  boolean: (value) => value === "true",
  number: (value) => Number(value),
  array: (value) => JSON.parse(value),
  arrayString: (value) =>
    value.split(",").map((item) => parseAttribute(item.trim())),
  object: (value) => JSON.parse(value),
  // TODO: Test
  // breakpoint: value => {
  //   if (typeof value === 'string') return token('breakpoint', 'min', value) ?? value
  //   if (typeof value === 'object') return generateMediaQuery(value)
  //   if (typeof value === 'number') return generateMediaQuery({ 'min-width': value })
  //   return value ?? false
  // },
};
export const MEDIA_QUERY_PROPERTY_ALIASES = {
  min: "min-width",
  max: "max-width",
};
export const PUB_SUB_EVENTS = {
  cartUpdate: "cart-update",
  quantityUpdate: "quantity-update",
  optionValueSelectionChange: "option-value-selection-change",
  variantChange: "variant-change",
  cartError: "cart-error",
  configValueChange: "config-value-change",
};
export const VIEW_CART_PARAM = "viewcart";
export const EXCLUDED_ANCHOR_LINKS = [
  "#recover",
  "#login",
  "#MainContent",
  "#my-account",
];
export const constants = /* @__PURE__ */ Object.freeze(
  /* @__PURE__ */ Object.defineProperty(
    {
      __proto__: null,
      BREAKPOINT_KEY_SEGMENTS,
      EXCLUDED_ANCHOR_LINKS,
      KEY_PATTERNS,
      MEDIA_ASPECT_RATIO_ALIASES,
      MEDIA_ASPECT_RATIO_DELIMITERS,
      MEDIA_QUERY_PROPERTY_ALIASES,
      ON_CHANGE_DEBOUNCE_TIMER,
      PUB_SUB_EVENTS,
      RESIZE_DEBOUNCE_TIMER,
      SLIDE_CHANGE_DEBOUNCE_TIMER,
      VALUE_PARSERS,
      VALUE_PATTERNS,
      VIEW_CART_PARAM,
    },
    Symbol.toStringTag,
    { value: "Module" },
  ),
);
export function clamp(num, min, max) {
  return Math.min(Math.max(num, min), max);
}
export function callOnReady(callback) {
  if (["complete", "loaded"].includes(document.readyState)) {
    callback();
  } else {
    document.addEventListener("DOMContentLoaded", callback);
  }
}
export function callOnLoad(callback) {
  if (document.readyState === "complete") {
    callback();
  } else {
    window.addEventListener("load", callback);
  }
}
export function debounce(fn, wait) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}
export function throttle(fn, delay) {
  let wait = false;
  return (...args) => {
    if (wait) return;
    fn(...args);
    wait = true;
    setTimeout(() => {
      wait = false;
    }, delay);
  };
}
export function curry(fn, { reverse = false } = {}, arity = fn.length) {
  return function curried(...args) {
    if (args.length >= arity) {
      if (reverse) args.reverse();
      return fn(...args);
    }
    return (...moreArgs) => curried(...args, ...moreArgs);
  };
}
export function partial(fn, ...args) {
  return (..._args) => fn(...args, ..._args);
}
export function range(_min, _max3, _step = 1) {
  let min = _min;
  let max = _max3;
  let step = _step;
  if (max === void 0) {
    max = min;
    min = step;
  }

  min = Number(min);
  max = Number(max);
  step = Number(step);
  return Array.from(
    { length: Math.floor((max - min) / step) + 1 },
    (_, i) => min + i * step,
  );
}
export function fromCamel(str, separator = "-") {
  return str === str.toLowerCase()
    ? str
    : str.replace(/[A-Z]/g, (m) => `${separator}${m.toLowerCase()}`);
}
export function toCamel(str) {
  if (/^[a-zA-Z]+$/.test(str)) return str;
  return str.toLowerCase().replace(/[-_\s]./g, (m) => m[1].toUpperCase());
}
export function parseAttribute(value, key) {
  if (typeof value === "undefined" || value === "null") return null;
  if (value === "") return true;
  let parsedValue = value;
  for (const VALUE_TYPE of Object.keys(VALUE_PATTERNS)) {
    if (VALUE_PATTERNS[VALUE_TYPE].test(parsedValue)) {
      parsedValue = VALUE_PARSERS[VALUE_TYPE](parsedValue);
    }
  }

  if (isBase64(parsedValue)) {
    parsedValue = parseBase64Object(parsedValue);
  }

  return parsedValue;
}
export const NOT_BASE64_PATTERN = /[^A-Z0-9+\/=]/i;
export const URLSAFE_BASE64_PATTERN = /^[A-Z0-9_\-]*$/i;
export const isBase64 = (value, urlSafe = false) => {
  if (!value) return false;
  if (urlSafe) return URLSAFE_BASE64_PATTERN.test(value);
  const length = value.length;
  if (length % 4 !== 0 || NOT_BASE64_PATTERN.test(value)) return false;
  const firstPaddingChar = value.indexOf("=");
  return (
    firstPaddingChar === -1 ||
    firstPaddingChar === length - 1 ||
    (firstPaddingChar === length - 2 && value[length - 1] === "=")
  );
};
export const parseBase64Object = (rawValue) => {
  const value = atob(rawValue);
  let parsedValue;
  try {
    parsedValue = JSON.parse(value);
  } catch (e) {
    parsedValue = rawValue;
  }

  return parsedValue;
};
export function blendEases(startEase, endEase, blender, startBlendProgress) {
  const s = gsap.parseEase(startEase);
  const e = gsap.parseEase(endEase);
  const p = 0;
  const blenderFunction = gsap.parseEase(blender);
  return (v) => {
    if (v <= p) return s(v);
    const b = blenderFunction((v - p) / (1 - p));
    return s(v) * (1 - b) + e(v) * b;
  };
}
export function isIterable(value) {
  return (
    value != null &&
    value !== void 0 &&
    typeof value[Symbol.iterator] === "function"
  );
}
export const defineElement = (element, source) => {
  if (!element) {
    console.error("Failed to define element. No tag name provided.");
    return;
  }

  if (!source) {
    console.error("Failed to define element. No source class provided.");
    return;
  }

  if (customElements.get(element)) return;
  customElements.define(element, source);
};
export const getElement = (element) => {
  if (customElements.get(element)) {
    return customElements.get(element);
  }

  return null;
};
export const getMatchingAttributes = (elements, callback, nameOnly = false) => {
  elements = Array.isArray(elements) ? elements : [elements];
  const attributes = elements.flatMap(element => [...element.attributes].filter(attr => callback(attr.name, attr.value)))

  return nameOnly ? attributes.map(attr => attr.name) : attributes;
}
